import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import TranslationForm from './components/TranslationForm';
import RequestsTable from './components/RequestsTable';
import RequestDetail from './components/RequestDetail';
import { getAllRequests, submitTranslation, getRequestStatus, saveToTranslationMemory } from './api';

function App() {
  const [activeTab, setActiveTab] = useState('submit');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [message, setMessage] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllRequests();
      setRequests(data);
      setLastRefresh(new Date());
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load requests: ' + error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchRequests();
    }
  }, [activeTab, fetchRequests]);

  const handleSubmit = async (formData) => {
    setLoading(true);
    setMessage(null);
    try {
      const result = await submitTranslation(formData);
      setMessage({ 
        type: 'success', 
        text: `Translation request submitted! Request ID: ${result.req_id}` 
      });
      return result;
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to submit: ' + error.message });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchRequests();
  };

  const handleViewRequest = async (request) => {
    try {
      const updated = await getRequestStatus(request.req_id);
      setSelectedRequest(updated);
    } catch (error) {
      setSelectedRequest(request);
    }
  };

  const handleSaveTranslation = async (reqId, source, target) => {
    try {
      await saveToTranslationMemory(source, target);
      setMessage({ type: 'success', text: 'Translation saved to Translation Memory!' });
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save: ' + error.message });
    }
  };

  const handleCloseModal = () => {
    setSelectedRequest(null);
  };

  return (
    <div className="app">
      <header className="header">
        <div className="header-logo">
          <span className="logo-placeholder">CIGNA</span>
          <h1>AI Translation Service</h1>
        </div>
        <span className="language-display">
          <span>English (en-US)</span>
          <span className="language-arrow">→</span>
          <span>Spanish (es-US)</span>
        </span>
      </header>

      <main className="main-container">
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === 'submit' ? 'active' : ''}`}
            onClick={() => setActiveTab('submit')}
          >
            Submit Translation
          </button>
          <button
            className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            View Requests
          </button>
        </div>

        {message && (
          <div className={`message message-${message.type}`}>
            {message.text}
            <button 
              onClick={() => setMessage(null)} 
              style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>
        )}

        {activeTab === 'submit' && (
          <TranslationForm onSubmit={handleSubmit} loading={loading} />
        )}

        {activeTab === 'requests' && (
          <RequestsTable
            requests={requests}
            loading={loading}
            onRefresh={handleRefresh}
            onViewRequest={handleViewRequest}
            lastRefresh={lastRefresh}
          />
        )}

        {selectedRequest && (
          <RequestDetail
            request={selectedRequest}
            onClose={handleCloseModal}
            onSave={handleSaveTranslation}
          />
        )}
      </main>
    </div>
  );
}

export default App;
